import { useToggle } from "hooks"
import { round } from "lodash-es"
import { useWindowWidth } from "materia"
import {
  createContext,
  useContext,
  useRef,
  useState,
  type MutableRefObject,
  type ReactNode,
} from "react"

export type PDFContextType = {
  // Meta
  containerRef: MutableRefObject<HTMLDivElement | null>
  isOutlineVisible: boolean
  toggleOutline: () => void
  // Zooming
  zoomFactor: number
  setZoomFactor: (zoomFactor: number) => void
  pageAspectRatio: number
  setPageAspectRatio: (pageAspectRatio: number) => void
  zoomIn: () => void
  zoomOut: () => void
  fitToWidth: () => void
  fitToHeight: () => void
  setInitialPageSize: (aspectRatio: number) => void
  // Navigation
  currentPageNumber: number
  numberOfPagesRef: MutableRefObject<number>
  navigateToPage: (pageNumber: number) => void
}

export const BASE_PAGE_WIDTH = 1024
export const MIN_ZOOM_FACTOR = 0.2
export const MAX_ZOOM_FACTOR = 2
export const ZOOM_STEP = 0.1

export const PDFContextProvider = ({ children }: { children: ReactNode }) => {
  const { isSmallWindow } = useWindowWidth()

  const containerRef = useRef<HTMLDivElement>(null)

  const numberOfPagesRef = useRef<number>(0) // must be a ref to not remain stale
  const [isOutlineVisible, toggleOutline] = useToggle(!isSmallWindow) // TODO Persist in local storage
  const [zoomFactor, setZoomFactor] = useState<number>(1)
  const [pageAspectRatio, setPageAspectRatio] = useState<number>(1)
  const [currentPageNumber, setCurrentPageNumber] = useState<number>(1)

  const navigateToPage = (pageNumber: number) => {
    if (!containerRef.current) return

    const isValidPage =
      pageNumber >= 1 && pageNumber <= numberOfPagesRef.current

    if (isValidPage) {
      setCurrentPageNumber(pageNumber)
      containerRef.current.scrollTo?.(0, 0)
    }
  }

  const zoomIn = () => {
    const newZoomFactor = round(zoomFactor + ZOOM_STEP, 1)
    setZoomFactor(Math.min(newZoomFactor, MAX_ZOOM_FACTOR))
  }

  const zoomOut = () => {
    const newZoomFactor = round(zoomFactor - ZOOM_STEP, 1)
    setZoomFactor(Math.max(newZoomFactor, MIN_ZOOM_FACTOR))
  }

  const fitToWidth = () => {
    if (!containerRef.current) return

    const newWidth = containerRef.current?.getBoundingClientRect()?.width ?? 0
    const newZoomFactor = newWidth / BASE_PAGE_WIDTH
    setZoomFactor(newZoomFactor)
  }

  const fitToHeight = () => {
    if (!containerRef.current) return

    const newHeight = containerRef.current?.getBoundingClientRect()?.height ?? 0
    const newZoomFactor = (pageAspectRatio * newHeight) / BASE_PAGE_WIDTH
    setZoomFactor(newZoomFactor)
  }

  const setInitialPageSize = (aspectRatio: number) => {
    if (!containerRef.current) return

    const boundingClientRect = containerRef.current.getBoundingClientRect()

    const newWidth = boundingClientRect?.width ?? 0
    const newHeight = boundingClientRect?.height ?? 0

    const heightZoomFactor = (aspectRatio * newHeight) / BASE_PAGE_WIDTH
    const widthZoomFactor = newWidth / BASE_PAGE_WIDTH

    const newZoomFactor = Math.min(heightZoomFactor, widthZoomFactor) // fits to page
    setZoomFactor(newZoomFactor)
  }

  const value: PDFContextType = {
    containerRef,
    zoomFactor,
    setZoomFactor,
    pageAspectRatio,
    setPageAspectRatio,
    zoomIn,
    zoomOut,
    fitToWidth,
    fitToHeight,
    setInitialPageSize,
    currentPageNumber,
    numberOfPagesRef,
    navigateToPage,
    isOutlineVisible,
    toggleOutline,
  }

  return <PDFContext.Provider value={value}>{children}</PDFContext.Provider>
}

const PDFContext = createContext({} as PDFContextType)
export const usePDFContext = (): PDFContextType => useContext(PDFContext)
